/**
 * STEP 3: DECISION DRIVERS
 * 
 * Priorities, concerns, must-haves
 * 
 * Fields:
 * - Top priority (with explanation)
 * - Biggest concern (with explanation)
 * - Must-haves (list)
 * - Nice-to-haves (list)
 * - Deal breakers (list)
 * 
 * Theme: Spec Ops
 */

import React, { useState } from 'react';
import StepContainer from './StepContainer';
import { Plus, X } from 'lucide-react';

const PRIORITIES = [
  { value: 'cost', label: 'Cost', description: 'Getting the best value for the budget' },
  { value: 'speed', label: 'Speed', description: 'Getting it done quickly' },
  { value: 'quality', label: 'Quality', description: 'Premium materials and craftsmanship' },
  { value: 'appearance', label: 'Appearance', description: 'Aesthetics and design' },
];

const CONCERNS = [
  { value: 'overruns', label: 'Cost Overruns', description: 'Budget staying on track' },
  { value: 'delays', label: 'Delays', description: 'Project running over schedule' },
  { value: 'mess', label: 'Mess/Disruption', description: 'Living through construction' },
  { value: 'quality', label: 'Quality Issues', description: 'Work not meeting expectations' },
  { value: 'communication', label: 'Communication', description: 'Staying informed and in the loop' },
  { value: 'access', label: 'Access/Privacy', description: 'Workers in their home' },
];

const Step3DecisionDrivers = ({ 
  data = {},
  onUpdate,
  onSave,
  onPrev,
  onNext,
  saving = false,
  isComplete = false,
  validationErrors = [],
}) => {
  const [newMustHave, setNewMustHave] = useState('');
  const [newNiceToHave, setNewNiceToHave] = useState('');
  const [newDealBreaker, setNewDealBreaker] = useState('');

  const updateField = (key, value) => {
    if (isComplete) return;
    onUpdate?.('decision_drivers', { ...data, [key]: value });
  };

  const addListItem = (listKey, value, clearFn) => {
    if (!value.trim() || isComplete) return;
    const currentList = data[listKey] || [];
    updateField(listKey, [...currentList, value.trim()]);
    clearFn('');
  };

  const removeListItem = (listKey, index) => {
    if (isComplete) return;
    const currentList = data[listKey] || [];
    updateField(listKey, currentList.filter((_, i) => i !== index));
  };

  return (
    <StepContainer
      step={3}
      onPrev={onPrev}
      onNext={onNext}
      onSave={onSave}
      isSaving={saving}
      isComplete={isComplete}
      validationErrors={validationErrors}
    >
      <div className="space-y-6 max-w-4xl">
        <p className="text-slate-400 text-sm">
          Internal notes - guides estimating priorities and scope tradeoffs.
        </p>

        {/* Priority */}
        <div className="space-y-4">
          <h3 className="text-emerald-400 font-semibold uppercase tracking-wider text-sm border-b border-slate-700 pb-2">
            Top Priority
          </h3>
          
          <div>
            <label className="block text-sm text-slate-400 mb-2">
              What&apos;s most important to this client? <span className="text-red-400">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {PRIORITIES.map(p => (
                <button
                  key={p.value}
                  onClick={() => updateField('priority', p.value)}
                  disabled={isComplete}
                  className={`
                    p-3 border text-left transition-all
                    ${data.priority === p.value 
                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' 
                      : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-600'
                    }
                    ${isComplete ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  <div className="font-medium">{p.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{p.description}</div>
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <label className="block text-sm text-slate-400 mb-1">
              Explain in their words
            </label>
            <textarea
              value={data.priority_explanation || ''}
              onChange={(e) => updateField('priority_explanation', e.target.value)}
              disabled={isComplete}
              rows={2}
              className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                focus:border-emerald-500 focus:outline-none disabled:opacity-50 resize-none"
              placeholder="What did they say about this priority?"
            />
          </div>
        </div>

        {/* Biggest Concern */}
        <div className="space-y-4">
          <h3 className="text-emerald-400 font-semibold uppercase tracking-wider text-sm border-b border-slate-700 pb-2">
            Biggest Concern
          </h3>
          
          <div>
            <label className="block text-sm text-slate-400 mb-2">
              What are they most worried about? <span className="text-red-400">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {CONCERNS.map(c => (
                <button
                  key={c.value}
                  onClick={() => updateField('biggest_concern', c.value)}
                  disabled={isComplete}
                  className={`
                    p-3 border text-left transition-all
                    ${data.biggest_concern === c.value 
                      ? 'border-amber-500 bg-amber-500/10 text-amber-400' 
                      : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-600'
                    }
                    ${isComplete ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  <div className="font-medium">{c.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{c.description}</div>
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <label className="block text-sm text-slate-400 mb-1">
              Explain in their words
            </label>
            <textarea
              value={data.biggest_concern_explanation || ''}
              onChange={(e) => updateField('biggest_concern_explanation', e.target.value)}
              disabled={isComplete}
              rows={2}
              className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                focus:border-emerald-500 focus:outline-none disabled:opacity-50 resize-none"
              placeholder="What specifically worries them?"
            />
          </div>
        </div>

        {/* Must Haves */}
        <div className="space-y-4">
          <h3 className="text-emerald-400 font-semibold uppercase tracking-wider text-sm border-b border-slate-700 pb-2">
            Must-Haves
          </h3>
          <p className="text-xs text-slate-500">Non-negotiable requirements - scope must include these</p>
          
          <div className="flex gap-2">
            <input
              type="text"
              value={newMustHave}
              onChange={(e) => setNewMustHave(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addListItem('must_haves', newMustHave, setNewMustHave)}
              disabled={isComplete}
              className="flex-1 px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              placeholder="Add a must-have..."
            />
            <button
              onClick={() => addListItem('must_haves', newMustHave, setNewMustHave)}
              disabled={isComplete || !newMustHave.trim()}
              className="px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-2">
            {(data.must_haves || []).map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-slate-900 border border-slate-700">
                <span className="flex-1 text-slate-200">{item}</span>
                {!isComplete && (
                  <button
                    onClick={() => removeListItem('must_haves', idx)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {(!data.must_haves || data.must_haves.length === 0) && (
              <p className="text-slate-500 text-sm italic">No must-haves added yet</p>
            )}
          </div>
        </div>

        {/* Nice to Haves */}
        <div className="space-y-4">
          <h3 className="text-slate-400 font-semibold uppercase tracking-wider text-sm border-b border-slate-700 pb-2">
            Nice-to-Haves
          </h3>
          <p className="text-xs text-slate-500">Desired but not required - include if budget allows</p>
          
          <div className="flex gap-2">
            <input
              type="text"
              value={newNiceToHave}
              onChange={(e) => setNewNiceToHave(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addListItem('nice_to_haves', newNiceToHave, setNewNiceToHave)}
              disabled={isComplete}
              className="flex-1 px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              placeholder="Add a nice-to-have..."
            />
            <button
              onClick={() => addListItem('nice_to_haves', newNiceToHave, setNewNiceToHave)}
              disabled={isComplete || !newNiceToHave.trim()}
              className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-2">
            {(data.nice_to_haves || []).map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-slate-900/50 border border-slate-800">
                <span className="flex-1 text-slate-300">{item}</span>
                {!isComplete && (
                  <button
                    onClick={() => removeListItem('nice_to_haves', idx)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {(!data.nice_to_haves || data.nice_to_haves.length === 0) && (
              <p className="text-slate-500 text-sm italic">No nice-to-haves added yet</p>
            )}
          </div>
        </div>

        {/* Deal Breakers */}
        <div className="space-y-4">
          <h3 className="text-red-400 font-semibold uppercase tracking-wider text-sm border-b border-slate-700 pb-2">
            Deal Breakers
          </h3>
          <p className="text-xs text-slate-500">Things that would kill the deal - avoid at all costs</p>
          
          <div className="flex gap-2">
            <input
              type="text"
              value={newDealBreaker}
              onChange={(e) => setNewDealBreaker(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addListItem('deal_breakers', newDealBreaker, setNewDealBreaker)}
              disabled={isComplete}
              className="flex-1 px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              placeholder="Add a deal breaker..."
            />
            <button
              onClick={() => addListItem('deal_breakers', newDealBreaker, setNewDealBreaker)}
              disabled={isComplete || !newDealBreaker.trim()}
              className="px-3 py-2 bg-red-600 hover:bg-red-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-2">
            {(data.deal_breakers || []).map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-red-900/20 border border-red-800/40">
                <span className="flex-1 text-red-200">{item}</span>
                {!isComplete && (
                  <button
                    onClick={() => removeListItem('deal_breakers', idx)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {(!data.deal_breakers || data.deal_breakers.length === 0) && (
              <p className="text-slate-500 text-sm italic">No deal breakers added yet</p>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-4">
          <h3 className="text-slate-400 font-semibold uppercase tracking-wider text-sm border-b border-slate-700 pb-2">
            Additional Notes
          </h3>
          <textarea
            value={data.notes || ''}
            onChange={(e) => updateField('notes', e.target.value)}
            disabled={isComplete}
            rows={3}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
              focus:border-emerald-500 focus:outline-none disabled:opacity-50 resize-none"
            placeholder="Any other decision driver notes..."
          />
        </div>
      </div>
    </StepContainer>
  );
};

export default Step3DecisionDrivers;
